/**
 * StaffSalaryService - Calculates staff salaries from teacher payment records
 * Matches EXACT logic from tablesalaries.js payslip calculation
 * 
 * Payslip Formula: Net Salary = basesalary + earning(bonuses) - earning(deductions)
 * Payment.amount = basesalary + bonuses - deductions (NET amount paid)
 */
class StaffSalaryService {
  // Helper function to sum amounts from an array (EXACT match from salaries page)
  earning(data) {
    if (!data || !Array.isArray(data)) return 0;
    let total = 0;
    for (let i = 0; i < data.length; i++) {
      total += parseFloat(data[i].amount) || 0;
    }
    return total;
  }

  // Calculate detailed staff salaries breakdown from payment records
  // Matches the exact calculation from tablesalaries.js payslip
  calculateStaffSalaries(teachers, startDate, endDate) {
    let totalSalaries = 0;
    let totalBaseSalaries = 0;
    let totalBonuses = 0;
    let totalDeductions = 0;  // Track deductions separately
    const byPosition = {};
    const staffDetails = [];

    // Normalize dates for comparison
    const normalizedStartDate = new Date(startDate);
    normalizedStartDate.setHours(0, 0, 0, 0);
    const normalizedEndDate = new Date(endDate);
    normalizedEndDate.setHours(23, 59, 59, 999);

    teachers.forEach(teacher => {
      let baseSalary = 0;
      let bonuses = 0;
      let deductions = 0;
      let totalSalary = 0;
      let hasPaymentsInPeriod = false;

      // Check if teacher has finance records (EXACT match from salaries page)
      if (teacher.finance && teacher.finance.length > 0) {
        const financeRecord = teacher.finance[0]; // Use first finance record like salaries page
        
        // PRIMARY METHOD: Check payment records (actual salary payments)
        // This matches how payslip calculates: payment.filter(bug => bug.paymonth === age)
        if (financeRecord.payment && Array.isArray(financeRecord.payment)) {
          
          financeRecord.payment.forEach(payment => {
            // Get payment date from payment.date or paymonth (EXACT match from salaries page)
            let paymentDate = null;
            
            if (payment.date) {
              paymentDate = new Date(payment.date);
            } else if (payment.paymonth) {
              // paymonth format: "2024-01" - convert to first day of month
              const [year, month] = payment.paymonth.split('-');
              if (year && month) {
                paymentDate = new Date(parseInt(year), parseInt(month) - 1, 1);
              }
            }
            
            // Check if payment is within date range
            if (paymentDate && !isNaN(paymentDate.getTime())) {
              const normalizedPaymentDate = new Date(paymentDate);
              normalizedPaymentDate.setHours(0, 0, 0, 0);
              
              // Check if payment month falls within date range
              // For paymonth, we check if the month is within the range
              const paymentMonth = paymentDate.getMonth();
              const paymentYear = paymentDate.getFullYear();
              const startMonth = normalizedStartDate.getMonth();
              const startYear = normalizedStartDate.getFullYear();
              const endMonth = normalizedEndDate.getMonth();
              const endYear = normalizedEndDate.getFullYear();
              
              // Check if payment month/year is within range
              const inRange = (
                (paymentYear > startYear || (paymentYear === startYear && paymentMonth >= startMonth)) &&
                (paymentYear < endYear || (paymentYear === endYear && paymentMonth <= endMonth))
              ) || (
                normalizedPaymentDate >= normalizedStartDate && normalizedPaymentDate <= normalizedEndDate
              );
              
              if (inRange) {
                hasPaymentsInPeriod = true;
                
                // EXACT calculation from payslip:
                // Net Salary = basesalary + earning(bonuses) - earning(deductions)
                // But payment.amount is already the NET amount (base + bonuses - deductions)
                
                // Get payment amount (this is the NET amount paid)
                const paymentAmount = parseFloat(payment.amount) || 0;
                
                // Get bonuses from payment.bonuses array (stored in payment record)
                // This matches: payment.filter(bug=>bug.paymonth===age)[0].bonuses
                let paymentBonuses = 0;
                if (payment.bonuses && Array.isArray(payment.bonuses)) {
                  paymentBonuses = this.earning(payment.bonuses);
                  bonuses += paymentBonuses;
                }
                
                // Get deductions from payment.deductions array
                // This matches: payment.filter(bug=>bug.paymonth===age)[0].deductions
                let paymentDeductions = 0;
                if (payment.deductions && Array.isArray(payment.deductions)) {
                  paymentDeductions = this.earning(payment.deductions);
                  deductions += paymentDeductions;
                }
                
                // Calculate base salary
                // From payslip: base = payment.amount - bonuses + deductions
                // (Because payment.amount = base + bonuses - deductions)
                const calculatedBase = paymentAmount - paymentBonuses + paymentDeductions;
                baseSalary += calculatedBase > 0 ? calculatedBase : 0;
                
                // Total salary is the NET amount (what was actually paid)
                totalSalary += paymentAmount;
              }
            }
          });
        }
        
        // FALLBACK: If no payments found in period, use finance record's basesalary and salarybonuses
        // This matches the logic when no payment records exist
        if (!hasPaymentsInPeriod) {
          const latestBaseSalary = parseFloat(financeRecord.basesalary) || 0;
          let latestBonuses = 0;
          let latestDeductions = 0;
          
          // Calculate bonuses from salarybonuses array (like earning() function)
          if (financeRecord.salarybonuses && Array.isArray(financeRecord.salarybonuses)) {
            latestBonuses = this.earning(financeRecord.salarybonuses);
          }
          
          // Calculate deductions from deductions array
          if (financeRecord.deductions && Array.isArray(financeRecord.deductions)) {
            latestDeductions = this.earning(financeRecord.deductions);
          }
          
          // Only add if we haven't found any payments (to avoid double counting)
          if (totalSalary === 0 && latestBaseSalary > 0) {
            baseSalary = latestBaseSalary;
            bonuses = latestBonuses;
            deductions = latestDeductions;
            // Net Salary = base + bonuses - deductions (EXACT match from salaries page)
            totalSalary = latestBaseSalary + latestBonuses - latestDeductions;
          }
        }
      } else {
        // Fallback to direct salary field if no finance records
        const salary = parseFloat(teacher.salary) || parseFloat(teacher.salaries) || parseFloat(teacher.monthlySalary) || 0;
        baseSalary = salary;
        totalSalary = salary;
      }

      // Only count if totalSalary > 0 (after deductions)
      if (totalSalary > 0) {
        totalSalaries += totalSalary;
        totalBaseSalaries += baseSalary;
        totalBonuses += bonuses;
        totalDeductions += deductions;

        const position = teacher.positiondescription || teacher.position || teacher.role || 'Staff';
        
        if (!byPosition[position]) {
          byPosition[position] = {
            count: 0,
            total: 0,
            base: 0,
            bonuses: 0,
            deductions: 0
          };
        }
        byPosition[position].count++;
        byPosition[position].total += totalSalary;
        byPosition[position].base += baseSalary;
        byPosition[position].bonuses += bonuses;
        byPosition[position].deductions += deductions;

        staffDetails.push({
          name: `${teacher.lastname || ''} ${teacher.othernames || ''}`.trim() || 'Unknown',
          position: position,
          baseSalary: baseSalary,
          bonuses: bonuses,
          deductions: deductions,
          totalSalary: totalSalary,  // This is NET salary (after deductions)
          staffID: teacher.ID || teacher._id,
          hasPayments: hasPaymentsInPeriod
        });
      }
    });

    // Add summary logging
    console.log('Staff Salaries Calculation Summary (Accounting Report):', {
      totalSalaries,      // NET total (after deductions)
      totalBaseSalaries,
      totalBonuses,
      totalDeductions,
      staffCount: staffDetails.length,
      byPosition,
      dateRange: { start: normalizedStartDate, end: normalizedEndDate }
    });

    return {
      total: totalSalaries,           // NET total (base + bonuses - deductions)
      totalBase: totalBaseSalaries,
      totalBonuses: totalBonuses,
      totalDeductions: totalDeductions,
      byPosition: byPosition,
      staffDetails: staffDetails.sort((a, b) => b.totalSalary - a.totalSalary),
      staffCount: staffDetails.length
    };
  }
}

export default new StaffSalaryService();
